import { Module, CreateModuleRequest, UpdateModuleRequest, ModuleInfo, ModuleStatistics, ModuleAchievement, ModuleState } from "./module.interfaces";
import { generateUID } from "@/lib/utils";

const mockModules: Module[] = [
  {
    id: generateUID(),
    name: "Mock Module 1",
    description: "This is a mock module 1",
    exercises: [],
    order: 1,
    status: "active",
    state: ModuleState.AVAILABLE,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: generateUID(),
    name: "Mock Module 2",
    description: "This is a mock module 2",
    exercises: [],
    order: 2,
    status: "active",
    state: ModuleState.AVAILABLE,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
];

// Mock module statistics
const mockModuleStatistics: ModuleStatistics = {
  exercisesCompleted: 32,
  totalExercises: 64,
  lessonsWatched: 12,
  totalLessons: 20,
  timeSpent: "8h 45min",
  averageScore: 85
};

// Mock module achievements
const mockModuleAchievements: ModuleAchievement[] = [
  {
    id: "achievement1",
    name: "Primeiro Quiz",
    icon: "CheckCircle",
    description: "Completou seu primeiro quiz com sucesso!"
  },
  {
    id: "achievement2",
    name: "Persistência",
    icon: "Clock",
    description: "Estudou por 5 dias consecutivos"
  },
  {
    id: "achievement3",
    name: "Vocabulário",
    icon: "Brain",
    description: "Aprendeu mais de 100 novas palavras"
  }
];

// Mapeamento de estados dos módulos por ID
// Os IDs são os IDs dos módulos nas URLs
const moduleStates: Record<string, ModuleState> = {
  "1": ModuleState.NOT_AVAILABLE,  // Módulo 1 - indisponível
  "2": ModuleState.AVAILABLE,      // Módulo 2 - disponível
  "3": ModuleState.IN_PROGRESS,    // Módulo 3 - em progresso
  "4": ModuleState.NOT_AVAILABLE,  // Módulo 4 - indisponível
  "5": ModuleState.AVAILABLE,      // Módulo 5 - disponível
  "6": ModuleState.IN_PROGRESS,    // Módulo 6 - em progresso
  "7": ModuleState.NOT_AVAILABLE,  // Módulo 7 - indisponível
  "8": ModuleState.AVAILABLE,      // Módulo 8 - disponível
  "mod-1-1": ModuleState.IN_PROGRESS,
  "mod-1-2": ModuleState.IN_PROGRESS,
  "mod-1-3": ModuleState.AVAILABLE,
  "mod-2-1": ModuleState.NOT_AVAILABLE
};

// Informações específicas dos módulos por ID
const moduleInfos: Record<string, Partial<ModuleInfo>> = {
  "1": { name: "Introdução ao Inglês", level: "Iniciante" },
  "2": { name: "Gramática Básica", level: "Iniciante" },
  "3": { name: "Conversação I", level: "Iniciante-Intermediário" },
  "4": { name: "Vocabulário Essencial", level: "Iniciante-Intermediário" },
  "5": { name: "Tempos Verbais", level: "Intermediário" },
  "6": { name: "Pronúncia e Fluência", level: "Intermediário" },
  "7": { name: "Inglês para Negócios", level: "Avançado" },
  "8": { name: "Cultura e Expressões", level: "Avançado" },
  "mod-1-1": { name: "Introdução à Gramática", level: "Básico" },
  "mod-1-2": { name: "Vocabulário Cotidiano", level: "Iniciante", description: "Aprenda palavras e expressões essenciais para comunicação diária" },
  "mod-1-3": { name: "Leitura e Compreensão", level: "Iniciante" },
  "mod-2-1": { name: "Estruturas Avançadas", level: "Intermediário" }
};

// Mock module info
const mockModuleInfo: ModuleInfo = {
  id: "4c5d3e27-745d-5d31-8f1f-bf73dda5905a",
  name: "Curso de Inglês I",
  description: "Este módulo foi projetado para iniciantes que desejam aprender inglês de forma interativa e divertida. Explore os exercícios para melhorar suas habilidades linguísticas.",
  level: "Iniciante",
  duration: "8 Semanas",
  hasCertificate: true,
  statistics: mockModuleStatistics,
  achievements: mockModuleAchievements,
  state: ModuleState.AVAILABLE // Default state for the mock
};

export const getModules = async (): Promise<Module[]> => {
  return mockModules;
};

export const getModule = async (id: string): Promise<Module> => {
  console.log(`Getting module with id: ${id}`);
  
  // Primeiro tenta encontrar o módulo exato
  const module = mockModules.find(m => m.id === id);
  
  if (module) {
    return module;
  }
  
  // Se o ID não for encontrado diretamente, cria um módulo dinamicamente
  // baseado nas informações do moduleInfos
  const moduleInfo = moduleInfos[id];
  if (moduleInfo) {
    console.log(`Creating dynamic module for id: ${id}`);
    return {
      id: id,
      name: moduleInfo.name || `Módulo ${id}`,
      description: moduleInfo.description || "Descrição não disponível para este módulo",
      exercises: [],
      order: parseInt(id.replace(/\D/g, '')) || 0,
      status: "active",
      state: moduleStates[id] || ModuleState.AVAILABLE,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }
  
  // Se ainda não encontrou, tenta extrair um ID mais simples
  if (id.includes('-')) {
    const simplerId = id.split('-')[1];
    console.log(`Trying simpler ID: ${simplerId}`);
    const fallbackModule = mockModules.find(m => m.id === simplerId);
    
    if (fallbackModule) {
      return {
        ...fallbackModule,
        id: id
      };
    }
  }
  
  // Se nada funcionar, cria um módulo padrão
  console.log(`Creating fallback module for id: ${id}`);
  return {
    id: id,
    name: `Módulo ${id}`,
    description: "Este é um módulo gerado automaticamente",
    exercises: [],
    order: 0,
    status: "active",
    state: moduleStates[id] || ModuleState.AVAILABLE,
    createdAt: new Date(),
    updatedAt: new Date()
  };
};

export const getModuleInfo = async (id: string): Promise<ModuleInfo> => {
  console.log(`Getting module info for id: ${id}`);
  
  // Obter o estado do módulo com base no ID
  const moduleState = moduleStates[id] || ModuleState.NOT_AVAILABLE;
  
  // Obter informações específicas do módulo com base no ID
  const moduleSpecificInfo = moduleInfos[id] || {};
  
  // Se tiver informações específicas, usa-as
  if (Object.keys(moduleSpecificInfo).length > 0) {
    console.log(`Found specific info for module ${id}`);
    return {
      ...mockModuleInfo,
      id: id,
      name: moduleSpecificInfo.name || `Módulo ${id}`,
      description: moduleSpecificInfo.description || mockModuleInfo.description,
      level: moduleSpecificInfo.level || mockModuleInfo.level,
      state: moduleState
    };
  }
  
  // Se o módulo tem um formato especial (ex: mod-1-2), tenta extrair partes
  if (id.includes('-')) {
    const parts = id.split('-');
    if (parts.length >= 3) {
      const packageNum = parts[1];
      const moduleNum = parts[2];
      
      console.log(`Parsed module ID: package ${packageNum}, module ${moduleNum}`);
      
      return {
        ...mockModuleInfo,
        id: id,
        name: `Módulo ${packageNum}.${moduleNum}`,
        description: `Este é o módulo ${moduleNum} do pacote ${packageNum}`,
        state: moduleState
      };
    }
  }
  
  // Fallback para ID genérico
  return {
    ...mockModuleInfo,
    id: id,
    name: `Módulo ${id}`,
    state: moduleState
  };
};

export const createModule = async (moduleData: CreateModuleRequest): Promise<Module> => {
  const newModule: Module = {
    ...moduleData,
    id: generateUID(),
    createdAt: new Date(),
    updatedAt: new Date()
  };
  mockModules.push(newModule);
  return newModule;
};

export const updateModule = async (id: string, moduleData: UpdateModuleRequest): Promise<Module> => {
  const moduleIndex = mockModules.findIndex(m => m.id === id);
  if (moduleIndex === -1) {
    throw new Error(`Module with id ${id} not found`);
  }
  
  const updatedModule = {
    ...mockModules[moduleIndex],
    ...moduleData,
    updatedAt: new Date()
  };
  
  mockModules[moduleIndex] = updatedModule;
  return updatedModule;
};

export const deleteModule = async (id: string): Promise<void> => {
  const moduleIndex = mockModules.findIndex(m => m.id === id);
  if (moduleIndex === -1) {
    throw new Error(`Module with id ${id} not found`);
  }
  
  mockModules.splice(moduleIndex, 1);
};
